import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaSave, FaFilePdf, FaEnvelope, FaPlus, FaTrash, FaTimes,
  FaSearch, FaBoxOpen, FaBuilding, FaUserTie, FaExclamationTriangle,
  FaImage, FaArrowLeft, FaSpinner
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import reklamacjeService from '../services/reklamacjeService';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import WyslijEmailModal from '../components/reklamacje/WyslijEmailModal';

/**
 * Formularz nowej / edycji reklamacji do dostawcy (kontrahenta).
 *
 * Tryb pracy zależy od parametru trasy :id
 *   - /reklamacje/nowa            -> tworzenie (brak id)
 *   - /reklamacje/:id/edytuj      -> edycja (id z useParams)
 *
 * Dane firmy zgłaszającej są pobierane informacyjnie (GET /api/admin/company,
 * dostępne dla admina; dla zwykłego usera endpoint zwraca 403 -> pokazujemy
 * notkę, że snapshot firmy backend robi automatycznie przy zapisie).
 *
 * Pozycje: każda ma własny wybór towaru (searchProdukty — BEZ filtra stanu,
 * dozwolony też ręczny wpis produktu spoza systemu) oraz własną fakturę zakupu.
 * Po wyborze produktu wołamy fakturyDlaProduktu — auto-podpinamy OSTATNIĄ FZ,
 * a użytkownik może wybrać inną z listy (pokazujemy OBA numery: nasz FZ-<id>
 * oraz numer dostawcy).
 *
 * Załączniki: w trybie edycji (istnieje id) wysyłamy pliki od razu; w trybie
 * tworzenia trzymamy je lokalnie i wysyłamy po utworzeniu reklamacji.
 * .heic/.heif nie mają podglądu w przeglądarce (backend konwertuje do JPEG) —
 * pokazujemy placeholder z nazwą pliku.
 */

// ── Słowniki UI ────────────────────────────────────────────────────────────
const RODZAJE_NIEPRAWIDLOWOSCI = [
  'Uszkodzenie mechaniczne',
  'Braki ilościowe',
  'Wada jakościowa',
  'Niezgodny towar (inny niż zamówiony)',
  'Towar przeterminowany',
  'Produkt wycofany z obrotu',
  'Uszkodzenie w transporcie',
  'Inne'
];

// Przewoznicy najczesciej uzywani. 'Inny' odslania pole tekstowe, zeby nie
// blokowac pozostalych (GLS, DPD, Poczta) ani nie zgubic wartosci zapisanych
// wczesniej recznie.
const PRZEWOZNICY = ['DHL', 'InPost', 'UPS'];

const SPOSOBY_ROZPATRZENIA = [
  'Wymiana towaru',
  'Korekta / zwrot pieniędzy',
  'Uznanie reklamacji',
  'Do rozpatrzenia przez dostawcę',
  'Naprawa',
  'Inne'
];

const PYTANIA_PRZESYLKA = [
  { key: 'przesylka_karton', label: 'Czy karton / opakowanie było uszkodzone?' },
  { key: 'przesylka_ingerencja', label: 'Czy widoczne były ślady ingerencji w przesyłkę?' },
  { key: 'przesylka_protokol', label: 'Czy spisano protokół szkody z przewoźnikiem?' }
];

const HEIC_RE = /\.(heic|heif)$/i;

// ── Style (tokeny kolorów, font 12px/11px zgodnie z UI standard) ────────────
const S = {
  page: { padding: '16px', maxWidth: '1100px', margin: '0 auto', background: 'var(--bg)', minHeight: '100vh' },
  card: { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', marginBottom: '16px' },
  sectionTitle: { fontSize: '13px', fontWeight: 700, color: 'var(--text)', margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: '8px' },
  grid: (cols) => ({ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '12px' }),
  label: { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' },
  input: {
    width: '100%', padding: '6px 10px', fontSize: '12px',
    border: '1px solid var(--border)', borderRadius: '6px',
    background: 'var(--card-bg)', color: 'var(--text)', boxSizing: 'border-box'
  },
  textarea: {
    width: '100%', padding: '6px 10px', fontSize: '12px', minHeight: '64px', resize: 'vertical',
    border: '1px solid var(--border)', borderRadius: '6px',
    background: 'var(--card-bg)', color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'inherit'
  },
  readonlyBox: {
    fontSize: '12px', color: 'var(--text)', background: 'var(--bg)',
    border: '1px dashed var(--border)', borderRadius: '6px', padding: '6px 10px', minHeight: '30px'
  },
  hint: { fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }
};

const btn = (color, filled = false, extra = {}) => ({
  padding: '8px 16px', fontSize: '12px', fontWeight: 600,
  border: `1px solid ${color}`, background: filled ? color : 'transparent',
  color: filled ? '#fff' : color, borderRadius: '6px', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
  ...extra
});

const smallBtn = (color, filled = false) => ({
  padding: '4px 10px', fontSize: '11px', fontWeight: 600,
  border: `1px solid ${color}`, background: filled ? color : 'transparent',
  color: filled ? '#fff' : color, borderRadius: '5px', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: '5px'
});

// ── Pusta pozycja ───────────────────────────────────────────────────────────
let POZ_SEQ = 1;
const nowaPozycja = (over = {}) => ({
  _key: `p${POZ_SEQ++}`,
  produkt_id: null,
  produkt_nazwa: '',
  produkt_kod: '',
  produkt_ean: '',
  jednostka: 'szt',
  ilosc: 1,
  faktura_zakupu_id: null,
  faktura_pozycja_id: null,
  wlasna_faktura: false,
  wlasny_numer_faktury: '',
  opis_pozycji: '',
  faktury: [],
  fakturyLoading: false,
  productResults: [],
  showProductDropdown: false,
  ...over
});

const emptyHeader = {
  kontrahent_typ: 'dostawca',
  // Powiazanie z kartoteka pos_klienci. NIE uzywamy juz 'kontrahent_id' - ta stara
  // kolumna trzyma historyczne id z martwej tabeli 'dostawcy' (wartosci 1-3) i po
  // podmianie zrodla wyszukiwarki zaczelaby oznaczac zupelnie inny podmiot. Backend
  // zdjal ja z listy pol edytowalnych, wiec jej nie wysylamy w ogole.
  kontrahent_klient_id: null,
  kontrahent_nazwa: '',
  kontrahent_nip: '',
  kontrahent_adres: '',
  kontrahent_email: '',
  osoba_imie_nazwisko: '',
  osoba_email: '',
  nr_wz: '',
  nr_fs: '',
  przewoznik: 'DHL',   // domyslny przewoznik dla nowej reklamacji
  rodzaj_nieprawidlowosci: 'Uszkodzenie mechaniczne',   // najczestszy przypadek
  opis_nieprawidlowosci: '',
  przesylka_karton: 'nd',
  przesylka_ingerencja: 'nd',
  przesylka_protokol: 'nd',
  sposob_rozpatrzenia: 'Korekta / zwrot pieniędzy',   // najczestszy przypadek
  uwagi: ''
};

const ReklamacjaFormPage = () => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(routeId);
  const { user } = useAuth();

  const [reklamacjaId, setReklamacjaId] = useState(routeId ? parseInt(routeId, 10) : null);
  const [numerReklamacji, setNumerReklamacji] = useState('');
  const [status, setStatus] = useState('nowa');

  const [header, setHeader] = useState(emptyHeader);
  const [pozycje, setPozycje] = useState([nowaPozycja()]);

  // Czy przewoznik jest spoza listy (wtedy pokazujemy pole tekstowe)
  const [innyPrzewoznik, setInnyPrzewoznik] = useState(false);
  // id reklamacji, dla ktorej otwarty jest modal wyboru adresatow (null = zamkniety)
  const [emailDlaId, setEmailDlaId] = useState(null);

  // Zglaszajacego podpowiadamy z konta zalogowanego kasjera (loginy w systemie to
  // imiona pracownikow). Tylko dla NOWEJ reklamacji i tylko gdy pole jest puste -
  // przy edycji nie wolno nadpisac osoby, ktora faktycznie zglaszala.
  useEffect(() => {
    if (isEdit || !user?.login) return;
    setHeader((prev) => (
      prev.osoba_imie_nazwisko ? prev : { ...prev, osoba_imie_nazwisko: user.login }
    ));
  }, [isEdit, user]);

  const [firma, setFirma] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  // Walidacja: zbior kluczy pol z bledem (do podswietlenia na czerwono).
  const [errors, setErrors] = useState({});

  // Kontrahent autocomplete
  const [kontrahentQuery, setKontrahentQuery] = useState('');
  const [kontrahentResults, setKontrahentResults] = useState([]);
  const [showKontrahentDropdown, setShowKontrahentDropdown] = useState(false);
  // Wyszukiwarka odpowiedziala pusta lista - pokazujemy podpowiedz, ze mozna wpisac
  // nazwe recznie (inaczej pusta lista wyglada jak awaria).
  const [kontrahentBrakWynikow, setKontrahentBrakWynikow] = useState(false);

  // Załączniki
  const [pendingFiles, setPendingFiles] = useState([]); // {file, previewUrl, isHeic} (tryb tworzenia lub przed wysyłką)
  const [zalaczniki, setZalaczniki] = useState([]); // {id, plik_nazwa, mime_type, previewUrl} — z serwera (tryb edycji)
  const [uploading, setUploading] = useState(false);

  const timers = useRef({});
  const fileInputRef = useRef(null);

  // ── Ładowanie danych firmy (best-effort, admin) ──────────────────────────
  useEffect(() => {
    let anulowane = false;
    (async () => {
      try {
        const res = await api.get('admin/company');
        if (!anulowane) setFirma(res?.data?.data?.company || null);
      } catch (e) {
        // 403 dla nie-admina — backend i tak zrobi snapshot firmy przy zapisie
        if (!anulowane) setFirma(null);
      }
    })();
    return () => { anulowane = true; };
  }, []);

  // ── Ładowanie reklamacji w trybie edycji ─────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    let anulowane = false;
    (async () => {
      setLoading(true);
      try {
        const res = await reklamacjeService.get(routeId);
        if (anulowane) return;
        const d = res?.data || {};
        const r = d.reklamacja || {};
        setReklamacjaId(r.id || parseInt(routeId, 10));
        setNumerReklamacji(r.numer_reklamacji || '');
        setStatus(r.status || 'nowa');
        setHeader({
          ...emptyHeader,
          ...Object.fromEntries(
            Object.keys(emptyHeader).map((k) => [k, r[k] ?? emptyHeader[k]])
          ),
          // Przy EDYCJI zadne podpowiedzi z emptyHeader nie moga sie podstawic -
          // liczy sie wylacznie to, co faktycznie zapisano w reklamacji.
          przewoznik: r.przewoznik ?? '',
          rodzaj_nieprawidlowosci: r.rodzaj_nieprawidlowosci ?? '',
          sposob_rozpatrzenia: r.sposob_rozpatrzenia ?? ''
        });
        // przewoznik spoza listy ma sie pokazac jako "Inny" z wpisana nazwa
        setInnyPrzewoznik(!!r.przewoznik && !PRZEWOZNICY.includes(r.przewoznik));
        setKontrahentQuery(r.kontrahent_nazwa || '');
        const poz = (d.pozycje || []).map((p) => nowaPozycja({
          produkt_id: p.produkt_id,
          produkt_nazwa: p.produkt_nazwa || '',
          produkt_kod: p.produkt_kod || '',
          produkt_ean: p.produkt_ean || '',
          jednostka: p.jednostka || 'szt',
          ilosc: p.ilosc ?? 1,
          faktura_zakupu_id: p.faktura_zakupu_id || null,
          faktura_pozycja_id: p.faktura_pozycja_id || null,
          // Reczny numer (spoza systemu): brak faktury z bazy, ale jest numer dostawcy.
          wlasna_faktura: !p.faktura_zakupu_id && !!p.dostawca_numer_faktury,
          wlasny_numer_faktury: (!p.faktura_zakupu_id && p.dostawca_numer_faktury) ? p.dostawca_numer_faktury : '',
          opis_pozycji: p.opis_pozycji || '',
          // odtwórz minimalny wpis faktury, by dropdown pokazał aktualne powiązanie
          faktury: p.faktura_zakupu_id ? [{
            faktura_zakupu_id: p.faktura_zakupu_id,
            faktura_pozycja_id: p.faktura_pozycja_id,
            fz_numer_wewnetrzny: p.fz_numer_wewnetrzny || `FZ-${p.faktura_zakupu_id}`,
            dostawca_numer_faktury: p.dostawca_numer_faktury || '',
            dostawca_nazwa: p.dostawca_nazwa || '',
            data_faktury: p.fz_data_faktury || '',
            ostatnia: false
          }] : []
        }));
        setPozycje(poz.length ? poz : [nowaPozycja()]);
        setZalaczniki((d.zalaczniki || []).map((z) => ({
          id: z.id,
          plik_nazwa: z.plik_nazwa,
          mime_type: z.mime_type,
          previewUrl: null
        })));
      } catch (err) {
        toast.error(err?.message || 'Błąd ładowania reklamacji');
      } finally {
        if (!anulowane) setLoading(false);
      }
    })();
    return () => { anulowane = true; };
  }, [routeId, isEdit]);

  // Podgląd miniatur załączników z serwera (autoryzowany blob)
  useEffect(() => {
    if (!isEdit || !reklamacjaId || zalaczniki.length === 0) return;
    let anulowane = false;
    const urls = [];
    (async () => {
      for (const z of zalaczniki) {
        if (z.previewUrl) continue;
        try {
          const { url } = await reklamacjeService.getZalacznikBlobUrl(reklamacjaId, z.id, true);
          if (anulowane) { URL.revokeObjectURL(url); return; }
          urls.push(url);
          setZalaczniki((prev) => prev.map((it) => it.id === z.id ? { ...it, previewUrl: url } : it));
        } catch (e) { /* pomijamy pojedynczy błąd miniatury */ }
      }
    })();
    return () => { anulowane = true; urls.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reklamacjaId, zalaczniki.length]);

  // Sprzątanie lokalnych object-URL-i
  useEffect(() => () => {
    pendingFiles.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
  }, [pendingFiles]);

  // ── Helpers stanu ────────────────────────────────────────────────────────
  const setH = (patch) => {
    setHeader((prev) => ({ ...prev, ...patch }));
    // Zdejmij podswietlenie bledu z edytowanego pola.
    setErrors((e) => {
      if (!Object.keys(e).length) return e;
      const n = { ...e }; Object.keys(patch).forEach((k) => delete n[k]); return n;
    });
  };
  const updatePozycja = (key, patch) => {
    setPozycje((prev) => prev.map((p) => (p._key === key ? { ...p, ...patch } : p)));
    // Edycja pozycji zdejmuje jej podswietlenia bledow.
    setErrors((e) => {
      if (!Object.keys(e).length) return e;
      const n = { ...e }; delete n.pozycje; delete n[`poz_${key}_wlasnyfv`]; return n;
    });
  };

  // ── Kontrahent search ────────────────────────────────────────────────────
  // Reczne wpisanie nazwy MUSI dzialac dalej - kartoteka jest podpowiedzia, nie
  // przymusem (walidacja wymaga wylacznie kontrahent_nazwa). Kazda zmiana tekstu
  // zrywa powiazanie z karta, zeby snapshot nie rozjechal sie z id.
  const onKontrahentQuery = (val) => {
    setKontrahentQuery(val);
    setH({ kontrahent_nazwa: val, kontrahent_klient_id: null });
    setKontrahentBrakWynikow(false);
    clearTimeout(timers.current.kontrahent);
    if (val.trim().length < 1) { setKontrahentResults([]); setShowKontrahentDropdown(false); return; }
    timers.current.kontrahent = setTimeout(async () => {
      try {
        // Bez parametru typu = wszyscy kontrahenci z kartoteki (dostawcy na gorze listy).
        const res = await reklamacjeService.searchKontrahenci(val.trim());
        const lista = res?.data?.kontrahenci || [];
        setKontrahentResults(lista);
        setKontrahentBrakWynikow(lista.length === 0);
        setShowKontrahentDropdown(true);
      } catch (e) { /* cicho */ }
    }, 300);
  };

  // Wybor karty przepisuje dane do naglowka JAKO SNAPSHOT - od tej chwili zyja one
  // w dokumencie i nie sa juz odswiezane z kartoteki (dokument sprzed roku ma
  // pokazywac dane z dnia wystawienia).
  const selectKontrahent = (k) => {
    const adres = [k.adres_ulica, [k.adres_kod, k.adres_miasto].filter(Boolean).join(' ')]
      .filter(Boolean).join(', ');
    setH({
      kontrahent_klient_id: k.id,
      kontrahent_nazwa: k.nazwa || '',
      kontrahent_nip: k.nip || '',
      kontrahent_adres: adres,
      kontrahent_email: k.email || ''
    });
    setKontrahentQuery(k.nazwa || '');
    setShowKontrahentDropdown(false);
    setKontrahentResults([]);
    setKontrahentBrakWynikow(false);
  };

  // Flagi rol z backendu bywaja jako 1/0, '1'/'0' albo true/false; brak pola = brak badge.
  const maFlage = (v) => v === true || v === 1 || v === '1';

  // Etykieta roli w podpowiedzi - uzytkownik ma widziec, z czym ma do czynienia.
  const RolaBadge = ({ label, color, bg }) => (
    <span style={{
      fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px',
      background: bg, color, whiteSpace: 'nowrap'
    }}>
      {label}
    </span>
  );

  // ── Produkt search (per pozycja) ─────────────────────────────────────────
  const onProductQuery = (key, val) => {
    updatePozycja(key, {
      produkt_nazwa: val, produkt_id: null, produkt_kod: '', produkt_ean: '',
      faktury: [], faktura_zakupu_id: null, faktura_pozycja_id: null
    });
    clearTimeout(timers.current['prod_' + key]);
    if (val.trim().length < 1) { updatePozycja(key, { productResults: [], showProductDropdown: false }); return; }
    timers.current['prod_' + key] = setTimeout(async () => {
      try {
        const res = await reklamacjeService.searchProdukty(val.trim());
        updatePozycja(key, { productResults: res?.data?.produkty || [], showProductDropdown: true });
      } catch (e) { /* cicho */ }
    }, 300);
  };

  const selectProduct = async (key, p) => {
    updatePozycja(key, {
      produkt_id: p.id,
      produkt_nazwa: p.nazwa || '',
      produkt_kod: p.kod_produktu || p.kod || '',
      produkt_ean: p.ean || '',
      jednostka: p.jednostka || 'szt',
      showProductDropdown: false,
      productResults: [],
      fakturyLoading: true,
      faktury: [],
      faktura_zakupu_id: null,
      faktura_pozycja_id: null
    });
    try {
      const res = await reklamacjeService.fakturyDlaProduktu(p.id);
      const faktury = res?.data?.faktury || [];
      const auto = faktury.find((f) => f.ostatnia) || faktury[0] || null;
      updatePozycja(key, {
        faktury,
        fakturyLoading: false,
        faktura_zakupu_id: auto ? auto.faktura_zakupu_id : null,
        faktura_pozycja_id: auto ? auto.faktura_pozycja_id : null,
        // Brak faktur z tym produktem -> od razu wlaczamy tryb recznego numeru,
        // zeby uzytkownik od razu widzial GDZIE wpisac numer (bez szukania checkboxa).
        ...(faktury.length ? {} : { wlasna_faktura: true, wlasny_numer_faktury: '' })
      });
      if (!faktury.length) {
        toast('Ten produkt nie występuje na żadnej fakturze zakupu — wpisz numer faktury ręcznie w polu poniżej.', { icon: 'ℹ️' });
      }
    } catch (err) {
      updatePozycja(key, { fakturyLoading: false });
      toast.error(err?.message || 'Błąd pobierania faktur dla produktu');
    }
  };

  const selectFaktura = (key, fzId) => {
    setPozycje((prev) => prev.map((p) => {
      if (p._key !== key) return p;
      const f = p.faktury.find((x) => String(x.faktura_zakupu_id) === String(fzId));
      return {
        ...p,
        faktura_zakupu_id: f ? f.faktura_zakupu_id : null,
        faktura_pozycja_id: f ? f.faktura_pozycja_id : null
      };
    }));
  };

  const addPozycja = () => setPozycje((prev) => [...prev, nowaPozycja()]);
  const removePozycja = (key) =>
    setPozycje((prev) => (prev.length <= 1 ? prev : prev.filter((p) => p._key !== key)));

  // ── Załączniki ───────────────────────────────────────────────────────────
  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (isEdit && reklamacjaId) {
      uploadFilesNow(files);
    } else {
      const mapped = files.map((file) => {
        const isHeic = HEIC_RE.test(file.name) || /heic|heif/i.test(file.type);
        return { file, isHeic, previewUrl: isHeic ? null : URL.createObjectURL(file) };
      });
      setPendingFiles((prev) => [...prev, ...mapped]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadFilesNow = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const res = await reklamacjeService.uploadZalacznik(reklamacjaId, file);
        const meta = res?.data || {};
        setZalaczniki((prev) => [...prev, {
          id: meta.id, plik_nazwa: meta.plik_nazwa || file.name,
          mime_type: meta.mime_type, previewUrl: null
        }]);
      }
      toast.success(`Dodano ${files.length} zdjęć`);
    } catch (err) {
      toast.error(err?.message || 'Błąd wysyłania załącznika');
    } finally {
      setUploading(false);
    }
  };

  const removePendingFile = (idx) =>
    setPendingFiles((prev) => {
      const f = prev[idx];
      if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });

  const removeZalacznik = async (zid) => {
    try {
      await reklamacjeService.deleteZalacznik(reklamacjaId, zid);
      setZalaczniki((prev) => {
        const z = prev.find((it) => it.id === zid);
        if (z?.previewUrl) URL.revokeObjectURL(z.previewUrl);
        return prev.filter((it) => it.id !== zid);
      });
      toast.success('Usunięto załącznik');
    } catch (err) {
      toast.error(err?.message || 'Błąd usuwania załącznika');
    }
  };

  const uploadPending = async (rid) => {
    if (!pendingFiles.length) return;
    setUploading(true);
    try {
      for (const pf of pendingFiles) {
        await reklamacjeService.uploadZalacznik(rid, pf.file);
      }
      pendingFiles.forEach((pf) => pf.previewUrl && URL.revokeObjectURL(pf.previewUrl));
      setPendingFiles([]);
    } catch (err) {
      toast.error('Reklamacja zapisana, ale część zdjęć się nie wysłała: ' + (err?.message || ''));
    } finally {
      setUploading(false);
    }
  };

  // ── Payload ──────────────────────────────────────────────────────────────
  const buildPayload = () => ({
    ...header,
    pozycje: pozycje
      .filter((p) => (p.produkt_nazwa || '').trim())
      .map((p) => ({
        produkt_id: p.produkt_id,
        produkt_nazwa: (p.produkt_nazwa || '').trim(),
        produkt_kod: p.produkt_kod || null,
        produkt_ean: p.produkt_ean || null,
        jednostka: p.jednostka || null,
        ilosc: Number(p.ilosc) || 1,
        // Wlasny numer faktury (spoza systemu) wyklucza fakture z bazy.
        faktura_zakupu_id: p.wlasna_faktura ? null : (p.faktura_zakupu_id || null),
        faktura_pozycja_id: p.wlasna_faktura ? null : (p.faktura_pozycja_id || null),
        wlasny_numer_faktury: p.wlasna_faktura ? ((p.wlasny_numer_faktury || '').trim() || null) : null,
        opis_pozycji: p.opis_pozycji || null
      }))
  });

  // Pola WYMAGANE (reszta - nr WZ/FS, przewoznik, uwagi, opis pozycji - opcjonalne).
  // Ilosc na fakturze bywa liczba dziesietna (np. 24.000) - obcinamy zbedne zera,
  // zeby wyswietlac "24 szt.", a nie "24.000 szt.".
  const formatIlosc = (v) => {
    const n = parseFloat(v);
    if (!isFinite(n)) return null;
    return String(parseFloat(n.toFixed(3)));
  };

  // Ile sztuk tego towaru bylo na fakturze WYBRANEJ w tej pozycji.
  // null = nie da sie ustalic (wlasny numer faktury albo brak wyboru) - wtedy
  // nie ma czego pilnowac i nie blokujemy zapisu.
  const iloscZFaktury = (p) => {
    if (p.wlasna_faktura || !p.faktura_zakupu_id) return null;
    const f = (p.faktury || []).find(
      (x) => String(x.faktura_zakupu_id) === String(p.faktura_zakupu_id)
    );
    if (!f) return null;
    const n = parseFloat(f.ilosc);
    return isFinite(n) ? n : null;
  };

  const REQUIRED_HEADER = [
    ['kontrahent_nazwa', 'Kontrahent (dostawca)'],
    ['osoba_imie_nazwisko', 'Imię i nazwisko zgłaszającego'],
    ['rodzaj_nieprawidlowosci', 'Rodzaj nieprawidłowości'],
    ['opis_nieprawidlowosci', 'Opis nieprawidłowości'],
    ['sposob_rozpatrzenia', 'Proponowany sposób rozpatrzenia'],
  ];

  const validate = () => {
    const errs = {};
    const missing = [];
    REQUIRED_HEADER.forEach(([key, label]) => {
      if (!(header[key] || '').toString().trim()) { errs[key] = true; missing.push(label); }
    });
    const validPoz = pozycje.filter((p) => (p.produkt_nazwa || '').trim());
    if (validPoz.length === 0) { errs.pozycje = true; missing.push('Co najmniej jedna pozycja z towarem'); }
    // Per pozycja: gdy zaznaczono wlasna faktura -> numer wymagany.
    pozycje.forEach((p) => {
      if ((p.produkt_nazwa || '').trim() && p.wlasna_faktura && !(p.wlasny_numer_faktury || '').trim()) {
        errs[`poz_${p._key}_wlasnyfv`] = true;
        missing.push('Własny numer faktury (uzupełnij lub odznacz)');
      }
      // BLOKADA: nie wolno zareklamowac wiecej sztuk, niz bylo na WYBRANEJ fakturze.
      // Sprawdzamy tylko pozycje powiazane z faktura z systemu - przy wlasnym numerze
      // faktury nie znamy ilosci, wiec nie ma czego pilnowac.
      const naFakturze = iloscZFaktury(p);
      if (naFakturze !== null && parseFloat(p.ilosc) > naFakturze) {
        errs[`poz_${p._key}_ilosc`] = true;
        missing.push(`Ilość większa niż na fakturze (max ${formatIlosc(naFakturze)} dla „${p.produkt_nazwa}")`);
      }
    });
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error(`Uzupełnij wymagane pola: ${missing.slice(0, 4).join(', ')}${missing.length > 4 ? '…' : ''}`);
      // przewin do pierwszego bledu
      setTimeout(() => {
        const el = document.querySelector('[data-err="1"]');
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return false;
    }
    return true;
  };

  // Styl podswietlenia pola z bledem + atrybut do przewijania. base = S.input / S.textarea.
  const errProps = (key, base = S.input) => (errors[key]
    ? { style: { ...base, borderColor: 'var(--danger)', boxShadow: '0 0 0 2px rgba(220,53,69,0.15)' }, 'data-err': '1' }
    : { style: base });

  // ── Zapis ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate() || saving) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit && reklamacjaId) {
        await reklamacjeService.update(reklamacjaId, payload);
        if (pendingFiles.length) await uploadPending(reklamacjaId);
        toast.success('Reklamacja zapisana');
      } else {
        const res = await reklamacjeService.create(payload);
        const newId = res?.data?.id;
        const newNr = res?.data?.numer_reklamacji;
        if (!newId) throw new Error('Serwer nie zwrócił identyfikatora reklamacji');
        if (pendingFiles.length) await uploadPending(newId);
        toast.success(`Utworzono reklamację ${newNr || ''}`.trim());
        // Przechodzimy w tryb edycji, aby udostępnić PDF / e-mail / dalsze załączniki
        navigate(`/reklamacje/${newId}/edytuj`, { replace: true });
      }
    } catch (err) {
      toast.error(err?.message || 'Błąd zapisu reklamacji');
    } finally {
      setSaving(false);
    }
  };

  const ensureSavedThen = async (fn) => {
    if (reklamacjaId) { fn(reklamacjaId); return; }
    toast.error('Najpierw zapisz reklamację');
  };

  // PDF wymaga kompletnego formularza (walidacja podswietla braki) i zapisu.
  const handlePdf = () => {
    if (!validate()) return;
    ensureSavedThen((rid) => navigate(`/reklamacje/${rid}/pdf-podglad`));
  };

  // Wysylka: najpierw zapis (jak PDF), potem modal z wyborem adresatow.
  const handleEmail = () => ensureSavedThen((rid) => {
    setEmailDlaId(rid);
  });

  const wyslijDoAdresatow = async (adresy) => {
    const t = toast.loading('Wysyłanie e-maila...');
    try {
      await reklamacjeService.sendEmail(emailDlaId, { do: adresy });
      toast.success(
        adresy.length === 1
          ? 'Reklamacja wysłana e-mailem'
          : `Reklamacja wysłana do ${adresy.length} adresatów`,
        { id: t }
      );
      setEmailDlaId(null);
    } catch (err) {
      toast.error(err?.message || 'Błąd wysyłki e-mail', { id: t });
    }
  };

  // ── Render pomocnicze ────────────────────────────────────────────────────
  const TakNieNd = ({ value, onChange }) => (
    <div style={{ display: 'flex', gap: '6px' }}>
      {[
        { v: 'tak', label: 'Tak', color: 'var(--danger)' },
        { v: 'nie', label: 'Nie', color: 'var(--success)' },
        { v: 'nd', label: 'N/D', color: 'var(--text-muted)' }
      ].map((opt) => (
        <button
          key={opt.v}
          type="button"
          onClick={() => onChange(opt.v)}
          style={smallBtn(opt.color, value === opt.v)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  const fakturaLabel = (f) => {
    const parts = [`FZ-${f.faktura_zakupu_id}`];
    if (f.dostawca_numer_faktury) parts.push(`nr dost.: ${f.dostawca_numer_faktury}`);
    // Ilosc z faktury - kasjer musi wiedziec, ile sztuk tego towaru bylo na dokumencie,
    // zeby nie zareklamowac wiecej, niz faktycznie przyszlo.
    const ilosc = formatIlosc(f.ilosc);
    const meta = [
      f.dostawca_nazwa,
      f.data_faktury,
      ilosc !== null ? `${ilosc} szt. na fakturze` : null
    ].filter(Boolean).join(' · ');
    let label = parts.join(' / ');
    if (meta) label += ` — ${meta}`;
    if (f.ostatnia) label += ' (ostatnia)';
    return label;
  };

  if (loading) {
    return (
      <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: 'var(--text-secondary)' }}>
        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> Ładowanie reklamacji...
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* Nagłówek strony + akcje */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button type="button" onClick={() => navigate('/reklamacje')} style={btn('var(--text-muted)')}>
            <FaArrowLeft /> Lista
          </button>
          <h2 style={{ margin: 0, fontSize: '18px', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaExclamationTriangle style={{ color: 'var(--warning)' }} />
            {isEdit ? `Reklamacja ${numerReklamacji || ''}` : 'Nowa reklamacja'}
          </h2>
          {isEdit && (
            <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--info)', border: '1px solid var(--info)', borderRadius: '20px', padding: '2px 10px' }}>
              {status.replace('_', ' ')}
            </span>
          )}
        </div>
      </div>

      {/* Dane firmy (readonly) */}
      <div style={S.card}>
        <h3 style={S.sectionTitle}><FaBuilding style={{ color: 'var(--info)' }} /> Dane firmy zgłaszającej</h3>
        {firma ? (
          <div style={S.grid(2)}>
            <div>
              <label style={S.label}>Nazwa</label>
              <div style={S.readonlyBox}>{firma.nazwa || '—'}</div>
            </div>
            <div>
              <label style={S.label}>NIP</label>
              <div style={S.readonlyBox}>{firma.nip || '—'}</div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={S.label}>Adres</label>
              <div style={S.readonlyBox}>
                {[firma.adres_ulica, [firma.adres_kod, firma.adres_miasto].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'}
              </div>
            </div>
          </div>
        ) : (
          <p style={S.hint}>Dane firmy zostaną automatycznie zapisane w reklamacji przy zapisie (snapshot z ustawień systemu).</p>
        )}
      </div>

      {/* Kontrahent */}
      <div style={S.card}>
        <h3 style={S.sectionTitle}><FaUserTie style={{ color: 'var(--warning)' }} /> Kontrahent (dostawca)</h3>
        <div style={S.grid(2)}>
          <div style={{ position: 'relative', gridColumn: '1 / -1' }}>
            <label style={S.label}>Nazwa kontrahenta *</label>
            <div style={{ position: 'relative' }}>
              <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '11px' }} />
              <input
                style={{ ...(errors.kontrahent_nazwa ? { ...S.input, borderColor: 'var(--danger)', boxShadow: '0 0 0 2px rgba(220,53,69,0.15)' } : S.input), paddingLeft: '28px' }}
                data-err={errors.kontrahent_nazwa ? '1' : undefined}
                value={kontrahentQuery}
                placeholder="Szukaj w kartotece (nazwa / NIP)..."
                onChange={(e) => onKontrahentQuery(e.target.value)}
                onFocus={() => kontrahentResults.length && setShowKontrahentDropdown(true)}
                onBlur={() => setTimeout(() => setShowKontrahentDropdown(false), 180)}
              />
            </div>
            {kontrahentBrakWynikow && (
              <div style={S.hint}>Brak kontrahenta w kartotece — możesz wpisać nazwę ręcznie.</div>
            )}
            {showKontrahentDropdown && kontrahentResults.length > 0 && (
              <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '6px', boxShadow: '0 4px 14px rgba(0,0,0,0.15)', maxHeight: '240px', overflowY: 'auto', marginTop: '2px' }}>
                {kontrahentResults.map((k) => (
                  <div
                    key={k.id}
                    onMouseDown={() => selectKontrahent(k)}
                    style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '12px', color: 'var(--text)' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }}>{k.nazwa}</span>
                      {/* Role przychodza z kartoteki jako flagi; podmiot moze miec obie. */}
                      {maFlage(k.jest_dostawca) && (
                        <RolaBadge label="Dostawca" color="var(--purple)" bg="var(--purple-light)" />
                      )}
                      {maFlage(k.jest_odbiorca) && (
                        <RolaBadge label="Klient" color="var(--info)" bg="var(--info-light)" />
                      )}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                      {[k.nip && `NIP: ${k.nip}`, k.adres_miasto].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label style={S.label}>NIP</label>
            <input style={S.input} value={header.kontrahent_nip} onChange={(e) => setH({ kontrahent_nip: e.target.value })} />
          </div>
          <div>
            <label style={S.label}>E-mail kontrahenta</label>
            <input style={S.input} type="email" value={header.kontrahent_email} onChange={(e) => setH({ kontrahent_email: e.target.value })} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={S.label}>Adres</label>
            <input style={S.input} value={header.kontrahent_adres} onChange={(e) => setH({ kontrahent_adres: e.target.value })} />
          </div>
        </div>
      </div>

      {/* Osoba zgłaszająca + dokumenty + przewoźnik */}
      <div style={S.card}>
        <h3 style={S.sectionTitle}><FaUserTie style={{ color: 'var(--info)' }} /> Osoba zgłaszająca i dokumenty</h3>
        <div style={S.grid(3)}>
          <div>
            <label style={S.label}>Imię i nazwisko <span style={{ color: 'var(--danger)' }}>*</span></label>
            <input {...errProps('osoba_imie_nazwisko')} value={header.osoba_imie_nazwisko} onChange={(e) => setH({ osoba_imie_nazwisko: e.target.value })} />
          </div>
          <div>
            <label style={S.label}>E-mail osoby</label>
            <input style={S.input} type="email" value={header.osoba_email} onChange={(e) => setH({ osoba_email: e.target.value })} />
          </div>
          <div>
            <label style={S.label}>Przewoźnik</label>
            <select
              style={S.input}
              value={innyPrzewoznik ? 'Inny' : header.przewoznik}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'Inny') {
                  setInnyPrzewoznik(true);
                  setH({ przewoznik: '' });
                } else {
                  setInnyPrzewoznik(false);
                  setH({ przewoznik: v });
                }
              }}
            >
              <option value="">— wybierz —</option>
              {PRZEWOZNICY.map((x) => <option key={x} value={x}>{x}</option>)}
              <option value="Inny">Inny…</option>
            </select>
            {innyPrzewoznik && (
              <input
                style={{ ...S.input, marginTop: '4px' }}
                placeholder="Nazwa przewoźnika"
                value={header.przewoznik}
                onChange={(e) => setH({ przewoznik: e.target.value })}
              />
            )}
          </div>
        </div>
      </div>

      {/* Nieprawidłowość */}
      <div style={S.card}>
        <h3 style={S.sectionTitle}><FaExclamationTriangle style={{ color: 'var(--danger)' }} /> Nieprawidłowość</h3>
        <div style={S.grid(2)}>
          <div>
            <label style={S.label}>Rodzaj nieprawidłowości <span style={{ color: 'var(--danger)' }}>*</span></label>
            <select {...errProps('rodzaj_nieprawidlowosci')} value={header.rodzaj_nieprawidlowosci} onChange={(e) => setH({ rodzaj_nieprawidlowosci: e.target.value })}>
              <option value="">— wybierz —</option>
              {RODZAJE_NIEPRAWIDLOWOSCI.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>Sposób rozpatrzenia <span style={{ color: 'var(--danger)' }}>*</span></label>
            <select {...errProps('sposob_rozpatrzenia')} value={header.sposob_rozpatrzenia} onChange={(e) => setH({ sposob_rozpatrzenia: e.target.value })}>
              <option value="">— wybierz —</option>
              {SPOSOBY_ROZPATRZENIA.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={S.label}>Opis nieprawidłowości <span style={{ color: 'var(--danger)' }}>*</span></label>
            <textarea {...errProps('opis_nieprawidlowosci', S.textarea)} value={header.opis_nieprawidlowosci} onChange={(e) => setH({ opis_nieprawidlowosci: e.target.value })} placeholder="Opisz na czym polega problem..." />
          </div>
        </div>

        {/* 3 pytania o przesyłkę */}
        <div style={{ marginTop: '14px', display: 'grid', gap: '10px' }}>
          {PYTANIA_PRZESYLKA.map((q) => (
            <div key={q.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: 'var(--text)' }}>{q.label}</span>
              <TakNieNd value={header[q.key]} onChange={(v) => setH({ [q.key]: v })} />
            </div>
          ))}
        </div>
      </div>

      {/* Pozycje */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <h3 style={{ ...S.sectionTitle, margin: 0 }}><FaBoxOpen style={{ color: 'var(--success)' }} /> Pozycje reklamacji</h3>
          <button type="button" onClick={addPozycja} style={smallBtn('var(--success)', true)}><FaPlus /> Dodaj pozycję</button>
        </div>

        {pozycje.map((p, idx) => (
          <div key={p._key} style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '12px', marginBottom: '12px', background: 'var(--bg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)' }}>Pozycja {idx + 1}</span>
              {pozycje.length > 1 && (
                <button type="button" onClick={() => removePozycja(p._key)} style={smallBtn('var(--danger)')}><FaTrash /> Usuń</button>
              )}
            </div>

            <div style={S.grid(2)}>
              {/* Produkt search */}
              <div style={{ position: 'relative', gridColumn: '1 / -1' }}>
                <label style={S.label}>Towar * <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(wyszukaj lub wpisz ręcznie — bez filtra stanu)</span></label>
                <div style={{ position: 'relative' }}>
                  <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '11px' }} />
                  <input
                    style={{ ...S.input, paddingLeft: '28px' }}
                    value={p.produkt_nazwa}
                    placeholder="Nazwa / EAN / kod towaru..."
                    onChange={(e) => onProductQuery(p._key, e.target.value)}
                    onFocus={() => p.productResults.length && updatePozycja(p._key, { showProductDropdown: true })}
                    onBlur={() => setTimeout(() => updatePozycja(p._key, { showProductDropdown: false }), 180)}
                  />
                </div>
                {p.produkt_id && (
                  <div style={S.hint}>
                    Powiązano z kartoteką: #{p.produkt_id}
                    {p.produkt_ean ? ` · EAN ${p.produkt_ean}` : ''}
                    {p.produkt_kod ? ` · kod ${p.produkt_kod}` : ''}
                  </div>
                )}
                {p.showProductDropdown && p.productResults.length > 0 && (
                  <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '6px', boxShadow: '0 4px 14px rgba(0,0,0,0.15)', maxHeight: '240px', overflowY: 'auto', marginTop: '2px' }}>
                    {p.productResults.map((prod) => (
                      <div
                        key={prod.id}
                        onMouseDown={() => selectProduct(p._key, prod)}
                        style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '12px', color: 'var(--text)' }}
                      >
                        <div style={{ fontWeight: 600 }}>{prod.nazwa}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          {[prod.ean && `EAN: ${prod.ean}`, prod.kod_produktu && `kod: ${prod.kod_produktu}`, (prod.stan !== undefined && prod.stan !== null) && `stan: ${prod.stan}`].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Faktura zakupu */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={S.label}>Faktura zakupu {p.fakturyLoading && <FaSpinner style={{ animation: 'spin 1s linear infinite', marginLeft: '6px' }} />}</label>
                {/* Checkbox: wpisz wlasny numer faktury (spoza systemu). Zaznaczenie wygasza
                    standardowy wybor i pozwala wpisac numer z innego systemu. */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', marginBottom: '6px', color: 'var(--text)' }}>
                  <input
                    type="checkbox"
                    checked={!!p.wlasna_faktura}
                    onChange={(e) => updatePozycja(p._key, {
                      wlasna_faktura: e.target.checked,
                      // przy wlaczeniu odpinamy fakture z systemu; przy wylaczeniu czyscimy wlasny numer
                      ...(e.target.checked
                        ? { faktura_zakupu_id: null, faktura_pozycja_id: null }
                        : { wlasny_numer_faktury: '' })
                    })}
                  />
                  Wpisz własny numer faktury (spoza systemu)
                </label>
                {p.wlasna_faktura ? (
                  <input
                    {...errProps(`poz_${p._key}_wlasnyfv`)}
                    type="text"
                    value={p.wlasny_numer_faktury || ''}
                    onChange={(e) => updatePozycja(p._key, { wlasny_numer_faktury: e.target.value })}
                    placeholder="np. FV/2026/06/812 — numer dokumentu z innego systemu"
                  />
                ) : p.faktury.length > 0 ? (
                  <select
                    style={S.input}
                    value={p.faktura_zakupu_id || ''}
                    onChange={(e) => selectFaktura(p._key, e.target.value)}
                  >
                    {!p.faktura_zakupu_id && <option value="">— wybierz fakturę —</option>}
                    {p.faktury.map((f) => (
                      <option key={f.faktura_zakupu_id} value={f.faktura_zakupu_id}>{fakturaLabel(f)}</option>
                    ))}
                  </select>
                ) : (
                  <div style={S.hint}>
                    {p.produkt_id ? 'Brak faktur zakupu z tym produktem — zaznacz „Wpisz własny numer faktury" powyżej.' : 'Po wybraniu towaru z kartoteki podpowiemy ostatnią fakturę zakupu.'}
                  </div>
                )}
              </div>

              {(() => {
                // Ilosc jest ograniczona tym, co faktycznie przyszlo na WYBRANEJ fakturze.
                // Wiecej sztuk = osobna pozycja z inna faktura (towar mogl przyjsc w kilku dostawach).
                const naFakturze = iloscZFaktury(p);
                const maxTekst = naFakturze !== null ? formatIlosc(naFakturze) : null;
                const zaduzo = naFakturze !== null && parseFloat(p.ilosc) > naFakturze;
                return (
                  <div>
                    <label style={S.label}>
                      Ilość
                      {maxTekst && (
                        <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
                          {' '}(max {maxTekst})
                        </span>
                      )}
                    </label>
                    <input
                      {...errProps(`poz_${p._key}_ilosc`)}
                      type="number"
                      min="0"
                      step="0.001"
                      max={naFakturze !== null ? naFakturze : undefined}
                      value={p.ilosc}
                      onChange={(e) => updatePozycja(p._key, { ilosc: e.target.value })}
                    />
                    {maxTekst && (
                      <div style={{
                        fontSize: '10px', marginTop: '3px',
                        color: zaduzo ? 'var(--danger)' : 'var(--text-muted)',
                        fontWeight: zaduzo ? '600' : 'normal'
                      }}>
                        {zaduzo
                          ? `⚠ Na tej fakturze było ${maxTekst} ${p.jednostka || 'szt.'}. Aby zareklamować więcej, dodaj ten sam towar jako kolejną pozycję i wskaż wcześniejszą fakturę.`
                          : `Na fakturze: ${maxTekst} ${p.jednostka || 'szt.'}`}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div>
                <label style={S.label}>Jednostka</label>
                <input style={S.input} value={p.jednostka} onChange={(e) => updatePozycja(p._key, { jednostka: e.target.value })} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={S.label}>Opis pozycji</label>
                <input style={S.input} value={p.opis_pozycji} onChange={(e) => updatePozycja(p._key, { opis_pozycji: e.target.value })} placeholder="Szczegóły dotyczące tej pozycji (opcjonalnie)" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Uwagi */}
      <div style={S.card}>
        <h3 style={S.sectionTitle}>Uwagi</h3>
        <textarea style={S.textarea} value={header.uwagi} onChange={(e) => setH({ uwagi: e.target.value })} placeholder="Dodatkowe uwagi (opcjonalnie)" />
      </div>

      {/* Załączniki */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h3 style={{ ...S.sectionTitle, margin: 0 }}><FaImage style={{ color: 'var(--info)' }} /> Zdjęcia / załączniki</h3>
          <div>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,.heic,.heif,image/heic,image/heif" multiple style={{ display: 'none' }} onChange={onPickFiles} />
            <button type="button" onClick={() => fileInputRef.current && fileInputRef.current.click()} style={smallBtn('var(--info)', true)} disabled={uploading}>
              {uploading ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaPlus />} Dodaj zdjęcia
            </button>
          </div>
        </div>

        {!isEdit && (
          <p style={S.hint}>Zdjęcia zostaną wysłane po zapisaniu reklamacji. Obsługiwane formaty: JPEG, PNG, WEBP, HEIC/HEIF (iPhone — konwersja po stronie serwera).</p>
        )}

        {(zalaczniki.length > 0 || pendingFiles.length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px', marginTop: '10px' }}>
            {/* Załączniki z serwera (edycja) */}
            {zalaczniki.map((z) => (
              <div key={`z${z.id}`} style={{ position: 'relative', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', background: 'var(--bg)' }}>
                <div style={{ width: '100%', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--card-bg)' }}>
                  {z.previewUrl
                    ? <img src={z.previewUrl} alt={z.plik_nazwa} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <FaImage style={{ fontSize: '28px', color: 'var(--text-muted)' }} />}
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', padding: '4px 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{z.plik_nazwa}</div>
                <button type="button" onClick={() => removeZalacznik(z.id)} title="Usuń" style={{ position: 'absolute', top: '4px', right: '4px', border: 'none', background: 'var(--danger)', color: '#fff', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}><FaTimes /></button>
              </div>
            ))}
            {/* Pliki oczekujące (tworzenie) */}
            {pendingFiles.map((pf, i) => (
              <div key={`pf${i}`} style={{ position: 'relative', border: '1px dashed var(--info)', borderRadius: '8px', overflow: 'hidden', background: 'var(--bg)' }}>
                <div style={{ width: '100%', height: '90px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--card-bg)' }}>
                  {pf.previewUrl
                    ? <img src={pf.previewUrl} alt={pf.file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <><FaImage style={{ fontSize: '26px', color: 'var(--text-muted)' }} /><span style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '4px' }}>HEIC</span></>}
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', padding: '4px 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pf.file.name}</div>
                <button type="button" onClick={() => removePendingFile(i)} title="Usuń" style={{ position: 'absolute', top: '4px', right: '4px', border: 'none', background: 'var(--danger)', color: '#fff', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}><FaTimes /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dolne akcje - wszystkie przyciski na samym dole formularza */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap', marginBottom: '24px' }}>
        <button type="button" onClick={() => navigate('/reklamacje')} style={btn('var(--text-muted)')}><FaTimes /> Anuluj</button>
        <button type="button" onClick={handleSave} disabled={saving} style={btn('var(--success)', true, saving ? { opacity: 0.6, cursor: 'wait' } : {})}>
          {saving ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaSave />} Zapisz
        </button>
        <button type="button" onClick={handlePdf} disabled={saving} style={btn('var(--danger)', false, saving ? { opacity: 0.6 } : {})}>
          <FaFilePdf /> Generuj PDF
        </button>
        <button type="button" onClick={handleEmail} disabled={!reklamacjaId} style={btn('var(--info)', false, !reklamacjaId ? { opacity: 0.5, cursor: 'not-allowed' } : {})}>
          <FaEnvelope /> Wyślij mailem
        </button>
      </div>

      <WyslijEmailModal
        isOpen={emailDlaId !== null}
        onClose={() => setEmailDlaId(null)}
        onWyslij={wyslijDoAdresatow}
        sugerowany={header.kontrahent_email || ''}
      />

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default ReklamacjaFormPage;
